<template>
  <div class="admin-create">
    <div class="page-header">
      <v-btn
        variant="text"
        color="#EA5545"
        class="back-btn"
        @click="router.back()"
      >
        <v-icon left>mdi-arrow-left</v-icon>
        Zurück
      </v-btn>
      <h1>Neuen Admin anlegen</h1>
      <p class="subtitle">
        Lege einen weiteren Admin für dein Unternehmen an. Der Benutzername wird aus dem Admin-Key
        und der Kundennummer gebildet.
      </p>
    </div>

    <v-card class="form-card" elevation="1">
      <v-card-text>
        <v-alert type="info" variant="tonal" class="mb-4">
          Gib eine Kundennummer ein. Der Benutzername lautet anschließend
          <strong>{{ previewUsername }}</strong>. Nach dem Anlegen wirst du automatisch zum neuen
          Benutzer weitergeleitet.
          <div class="hint-key" v-if="adminKey">
            Aktueller Admin-Key: <strong>{{ adminKey }}</strong>
          </div>
        </v-alert>

        <v-text-field
          v-model="customerNumber"
          label="Kundennummer"
          placeholder="z.B. 4711"
          variant="outlined"
          :disabled="isSubmitting"
          :error="!!errorText"
          @input="errorText = ''"
        />

        <div class="actions">
          <v-btn
            color="#EA5545"
            variant="flat"
            :disabled="!customerNumber || isSubmitting"
            :loading="isSubmitting"
            @click="createAdmin"
          >
            <v-icon left size="18">mdi-account-plus</v-icon>
            Admin erstellen
          </v-btn>
        </div>

        <v-alert
          v-if="errorText"
          type="error"
          variant="tonal"
          class="mt-4"
        >
          {{ errorText }}
        </v-alert>

        <v-alert
          v-if="successText"
          type="success"
          variant="tonal"
          class="mt-4"
        >
          {{ successText }}
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { inject, ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')
const router = useRouter()

const customerNumber = ref('')
const isSubmitting = ref(false)
const errorText = ref('')
const successText = ref('')
const adminKey = ref('')

const previewUsername = computed(() => {
  const key = adminKey.value || 'admin'
  const value = customerNumber.value.replace(/\s+/g, '')
  if (!value) return `${key} + Nummer`
  return `${key}${value}`
})

onMounted(() => {
  fetchAdminKey()
})

async function fetchAdminKey() {
  try {
    const response = await baseCalls.userPost({ action: 'getcompanyadminkey' })
    if (response.data?.success && response.data?.admin_key) {
      adminKey.value = response.data.admin_key
    }
  } catch (error) {
    console.error('fetchAdminKey', error)
  }
}

async function createAdmin() {
  const number = customerNumber.value.trim()
  if (!number) {
    errorText.value = 'Bitte eine Kundennummer angeben.'
    return
  }
  isSubmitting.value = true
  errorText.value = ''
  successText.value = ''
  try {
    const response = await baseCalls.userPost({
      action: 'createcompanyadmin',
          customer_number: number
    })

    if (response.data?.success) {
      successText.value = `Admin ${response.data.username} wurde angelegt. Du wirst weitergeleitet …`
      const targetId = response.data.user_id
      setTimeout(() => {
        if (targetId) {
          router.push({ name: 'adminuserdetail', params: { id: targetId } })
        } else if (response.data.redirect) {
          router.push(response.data.redirect)
        }
      }, 1200)
    } else {
      errorText.value = response.data?.message || 'Der Admin konnte nicht angelegt werden.'
    }
  } catch (error) {
    console.error('createAdmin', error)
    errorText.value = 'Es ist ein unbekannter Fehler aufgetreten.'
    errorStore?.setError?.(true, 'Admin konnte nicht angelegt werden.')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped src="@/assets/styles/sfc/components/sites/AdminCreateAdmin.css"></style>
